import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { RefreshCw, Database, Clock, Search, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Debounce function for search
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

function App() {
  const [students, setStudents] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 50,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [perPage, setPerPage] = useState(50);
  
  // Debounce search query
  const debouncedSearch = useDebounce(searchQuery, 500);

  const fetchStudents = useCallback(async (page = 1, showToast = false) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/students`, {
        params: {
          page,
          per_page: perPage,
          search: debouncedSearch
        }
      });
      
      setStudents(response.data.data);
      setPagination(response.data.pagination);
      setLastUpdate(new Date());
      setLoading(false);
      setRefreshing(false);
      
      if (showToast) {
        toast.success("Data refreshed successfully");
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      setLoading(false);
      setRefreshing(false);
      toast.error("Failed to fetch data");
    }
  }, [perPage, debouncedSearch]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleManualRefresh = () => {
    setRefreshing(true);
    fetchStudents(pagination.page, true);
    fetchStats();
  };

  const handlePageChange = (newPage) => {
    fetchStudents(newPage);
  };

  const handlePerPageChange = (value) => {
    setPerPage(Number(value));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Fetch data when debounced search, page, or perPage changes
  useEffect(() => {
    fetchStudents(1);
  }, [fetchStudents, debouncedSearch, perPage]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    fetchStats();
    
    const interval = setInterval(() => {
      fetchStudents(pagination.page, false);
      fetchStats();
    }, 300000); // 5 minutes

    return () => clearInterval(interval);
  }, [pagination.page, fetchStudents]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      // Check if it's in MM/DD/YYYY format
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const [month, day, year] = parts;
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
      }
      // Fallback for other formats
      const date = new Date(dateStr);
      const d = date.getDate();
      const m = date.getMonth() + 1;
      const y = date.getFullYear();
      return `${d.toString().padStart(2, '0')}/${m.toString().padStart(2, '0')}/${y}`;
    } catch {
      return dateStr;
    }
  };

  const formatLastUpdate = () => {
    if (!lastUpdate) return "Never";
    const now = new Date();
    const diff = Math.floor((now - lastUpdate) / 1000);
    if (diff < 60) return `${diff}s ago`;
    return `${Math.floor(diff / 60)}m ago`;
  };

  return (
    <div className="App" data-testid="student-dashboard">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
                Doodhnath Group - Digishakti Data
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 mt-1 sm:mt-2 tracking-wide uppercase">
                Live Data Dashboard
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleManualRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-sm hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="manual-refresh-button"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="text-sm font-medium hidden sm:inline">Refresh</span>
              </button>
              <div className="flex items-center gap-2 text-slate-500">
                <div className="flex items-center gap-2 animate-pulse">
                  <div className="w-2 h-2 rounded-full bg-orange-500" data-testid="live-indicator"></div>
                  <span className="text-xs uppercase tracking-wider font-medium hidden sm:inline">Live</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="bg-[#F8FAFC] min-h-screen pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-6 md:py-8 lg:py-12">
          
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
            <div className="bg-white border border-slate-200 shadow-sm p-4 md:p-6 rounded-none" data-testid="total-records-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-slate-500 uppercase tracking-wide font-medium">Total Records</p>
                  <p className="text-2xl md:text-3xl font-bold text-slate-900 mt-2">
                    {pagination.total_count}
                  </p>
                  {searchQuery && (
                    <p className="text-xs text-slate-400 mt-1">
                      Showing {students.length} filtered
                    </p>
                  )}
                </div>
                <Database className="w-6 h-6 md:w-8 md:h-8 text-slate-400" />
              </div>
            </div>
            
            <div className="bg-white border border-slate-200 shadow-sm p-4 md:p-6 rounded-none" data-testid="last-updated-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-slate-500 uppercase tracking-wide font-medium">Last Updated</p>
                  <p className="text-lg md:text-xl font-medium text-slate-900 mt-2">
                    {formatLastUpdate()}
                  </p>
                </div>
                <Clock className="w-6 h-6 md:w-8 md:h-8 text-slate-400" />
              </div>
            </div>
            
            <div className="bg-white border border-slate-200 shadow-sm p-4 md:p-6 rounded-none" data-testid="polling-status-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-slate-500 uppercase tracking-wide font-medium">Auto Refresh</p>
                  <p className="text-lg md:text-xl font-medium text-slate-900 mt-2">
                    Every 5 min
                  </p>
                </div>
                <RefreshCw className="w-6 h-6 md:w-8 md:h-8 text-slate-400" />
              </div>
            </div>
          </div>

          {/* Search & Pagination Controls */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-none p-4 mb-6" data-testid="controls-section">
            <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by Enrollment Number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent text-slate-900 text-sm md:text-base"
                  data-testid="enrollment-search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="clear-search-button"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
              
              {/* Per Page Selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600 whitespace-nowrap">Show:</span>
                <select
                  value={perPage}
                  onChange={(e) => handlePerPageChange(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm"
                  data-testid="per-page-selector"
                >
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span className="text-sm text-slate-600">per page</span>
              </div>
            </div>
          </div>

          {/* Data Table */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-none overflow-hidden" data-testid="students-table-container">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
                <span className="ml-3 text-slate-500">Loading data...</span>
              </div>
            ) : students.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 px-6">
                <Database className="w-16 h-16 text-slate-300 mb-4" />
                <p className="text-slate-500 text-lg">
                  {searchQuery ? "No records found" : "No records available"}
                </p>
                <p className="text-slate-400 text-sm mt-2">
                  {searchQuery ? "Try a different search term" : "Add data to the Google Sheet to see it here"}
                </p>
              </div>
            ) : (
              <>
                {/* Mobile scroll hint */}
                <div className="block md:hidden bg-orange-50 border-l-4 border-orange-500 p-3 mb-2">
                  <p className="text-xs text-orange-800">
                    ← Swipe left to see more columns →
                  </p>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left border-collapse" data-testid="students-table">
                    <thead>
                      <tr>
                        <th className="bg-slate-50 text-slate-500 uppercase tracking-wider font-medium py-3 md:py-4 px-3 md:px-6 border-b border-slate-200 sticky top-0 z-10 text-xs md:text-sm">
                          #
                        </th>
                        <th className="bg-slate-50 text-slate-500 uppercase tracking-wider font-medium py-3 md:py-4 px-3 md:px-6 border-b border-slate-200 sticky top-0 z-10 text-xs md:text-sm">
                          Enrollment
                        </th>
                        <th className="bg-slate-50 text-slate-500 uppercase tracking-wider font-medium py-3 md:py-4 px-3 md:px-6 border-b border-slate-200 sticky top-0 z-10 text-xs md:text-sm">
                          Name
                        </th>
                        <th className="bg-slate-50 text-slate-500 uppercase tracking-wider font-medium py-3 md:py-4 px-3 md:px-6 border-b border-slate-200 sticky top-0 z-10 text-xs md:text-sm">
                          Father's Name
                        </th>
                        <th className="bg-slate-50 text-slate-500 uppercase tracking-wider font-medium py-3 md:py-4 px-3 md:px-6 border-b border-slate-200 sticky top-0 z-10 text-xs md:text-sm">
                          Mobile
                        </th>
                        <th className="bg-slate-50 text-slate-500 uppercase tracking-wider font-medium py-3 md:py-4 px-3 md:px-6 border-b border-slate-200 sticky top-0 z-10 text-xs md:text-sm">
                          Email
                        </th>
                        <th className="bg-slate-50 text-slate-500 uppercase tracking-wider font-medium py-3 md:py-4 px-3 md:px-6 border-b border-slate-200 sticky top-0 z-10 text-xs md:text-sm">
                          DOB
                        </th>
                        <th className="bg-slate-50 text-slate-500 uppercase tracking-wider font-medium py-3 md:py-4 px-3 md:px-6 border-b border-slate-200 sticky top-0 z-10 text-xs md:text-sm">
                          Class
                        </th>
                        <th className="bg-slate-50 text-slate-500 uppercase tracking-wider font-medium py-3 md:py-4 px-3 md:px-6 border-b border-slate-200 sticky top-0 z-10 text-xs md:text-sm">
                          Semester
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student, index) => (
                        <tr 
                          key={student.id} 
                          className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors duration-150"
                          data-testid={`student-row-${index}`}
                        >
                          <td className="py-3 md:py-4 px-3 md:px-6 align-middle text-slate-700 text-xs md:text-sm">
                            {(pagination.page - 1) * pagination.per_page + index + 1}
                          </td>
                          <td className="py-3 md:py-4 px-3 md:px-6 align-middle font-medium text-slate-900 text-xs md:text-sm">
                            {student.enrollment_number || "N/A"}
                          </td>
                          <td className="py-3 md:py-4 px-3 md:px-6 align-middle font-medium text-slate-900 text-xs md:text-sm">
                            {student.name}
                          </td>
                          <td className="py-3 md:py-4 px-3 md:px-6 align-middle text-slate-700 text-xs md:text-sm">
                            {student.father_name || "N/A"}
                          </td>
                          <td className="py-3 md:py-4 px-3 md:px-6 align-middle font-mono text-slate-700 text-xs md:text-sm">
                            {student.mobile}
                          </td>
                          <td className="py-3 md:py-4 px-3 md:px-6 align-middle font-mono text-slate-700 text-xs md:text-sm">
                            {student.email}
                          </td>
                          <td className="py-3 md:py-4 px-3 md:px-6 align-middle text-slate-700 text-xs md:text-sm">
                            {formatDate(student.date_of_birth)}
                          </td>
                          <td className="py-3 md:py-4 px-3 md:px-6 align-middle text-slate-700 text-xs md:text-sm">
                            {student.class_name || "N/A"}
                          </td>
                          <td className="py-3 md:py-4 px-3 md:px-6 align-middle text-slate-700 text-xs md:text-sm">
                            {student.semester || "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                <div className="border-t border-slate-200 bg-slate-50 px-4 py-4">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-sm text-slate-600">
                      Showing {((pagination.page - 1) * pagination.per_page) + 1} to {Math.min(pagination.page * pagination.per_page, pagination.total_count)} of {pagination.total_count} records
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={!pagination.has_prev}
                        className="px-3 py-2 border border-slate-200 rounded-sm hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                        data-testid="prev-page-button"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        <span className="hidden sm:inline text-sm">Previous</span>
                      </button>
                      
                      <div className="flex items-center gap-2">
                        {[...Array(Math.min(5, pagination.total_pages))].map((_, idx) => {
                          let pageNum;
                          if (pagination.total_pages <= 5) {
                            pageNum = idx + 1;
                          } else if (pagination.page <= 3) {
                            pageNum = idx + 1;
                          } else if (pagination.page >= pagination.total_pages - 2) {
                            pageNum = pagination.total_pages - 4 + idx;
                          } else {
                            pageNum = pagination.page - 2 + idx;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`px-3 py-2 border rounded-sm text-sm transition-colors ${
                                pagination.page === pageNum
                                  ? 'bg-slate-900 text-white border-slate-900'
                                  : 'border-slate-200 hover:bg-white'
                              }`}
                              data-testid={`page-${pageNum}-button`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={!pagination.has_next}
                        className="px-3 py-2 border border-slate-200 rounded-sm hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                        data-testid="next-page-button"
                      >
                        <span className="hidden sm:inline text-sm">Next</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;
